/**
 * Maps BPO service page slugs (/services/bpo/<slug>) to the /start BPO wizard
 * service ids. Only unambiguous matches are listed: a slug absent here and
 * absent from the wizard ids leaves the service unselected (never guessed).
 */
export const BPO_WIZARD_SERVICE_IDS = [
  'inbound-customer-care', 'technical-support', 'outbound-sales', 'lead-qualification',
  'backoffice', 'order-management', 'live-chat', 'digital-engagement', 'social-media',
  'multilingual', 'market-research', 'content-moderation', 'trust-safety', 'qa-coaching',
  'collections', 'billing-collections', 'customer-retention', 'virtual-assistant',
] as const;

export const BPO_PAGE_SLUG_ALIASES: Record<string, string> = {
  'customer-support': 'inbound-customer-care',
  'inbound-customer-support': 'inbound-customer-care',
  // CC-1910: /call-center is a general Call Center & BPO offer, not inbound care — kept as a named topic, never guessed.
  'phone-support': 'inbound-customer-care',
  '24-7-support': 'inbound-customer-care',
  'sales-sdr': 'lead-qualification',
  'sdr-appointment-setting': 'lead-qualification',
  'retention': 'customer-retention',
  'retention-winback': 'customer-retention',
  'trust-safety-page': 'trust-safety',
  'trust-safety-compliance': 'trust-safety',
  'market-research-surveys': 'market-research',
  'ai-ops-backoffice': 'backoffice',
  'back-office-data-processing': 'backoffice',
  'data-entry-processing': 'backoffice',
  'multilingual-support': 'multilingual',
  'live-chat-messaging': 'live-chat',
  'chat-email-support': 'live-chat',
  'whatsapp-business-support': 'live-chat',
  'social-media-care': 'social-media',
  'social-media-management': 'social-media',
  'collections-payment': 'collections',
  'content-review': 'content-moderation',
  // Exact semantic matches with an existing catalogue entry
  'appointment': 'lead-qualification',
  'omnichannel': 'digital-engagement',
  // LIVECHAT-2410: service landing page slugs → their exact catalogue service,
  // so the /start widget keeps the requested service instead of "to define".
  'live-chat-support-outsourcing': 'live-chat',
  'live-chat-social-media-support': 'live-chat',
  'customer-support-outsourcing': 'inbound-customer-care',
  'technical-support-outsourcing': 'technical-support',
  'appointment-setting-services': 'lead-qualification',
  'back-office-outsourcing': 'backoffice',
  'multilingual-support-services': 'multilingual',
  'order-management-outsourcing': 'order-management',
  'telemarketing-services': 'outbound-sales',
  'virtual-assistant-services': 'virtual-assistant',
  'trust-safety-content-moderation': 'trust-safety',
};

export function resolveBpoWizardService(slug: string | null | undefined): string | null {
  if (!slug) return null;
  const v = slug.trim().toLowerCase();
  const id = BPO_PAGE_SLUG_ALIASES[v] || v;
  return (BPO_WIZARD_SERVICE_IDS as readonly string[]).includes(id) ? id : null;
}

/**
 * Honest fallback for a BPO page without a catalogue equivalent: no package,
 * no price, no contractual service is invented — the need is scoped together
 * and the page the visitor asked about is kept verbatim on the request.
 */
export const BPO_TO_DEFINE_ID = 'to-define';

/** Keeps only a safe slug (a-z, 0-9, dash), max 60 chars; null if empty. */
export function sanitizeRequestedTopic(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 60);
  return v || null;
}

/** service= value from a BPO link → wizard selection (exact service or "to define" + topic). */
export function bpoSelectionFromLink(rawService: string | null | undefined): { service: string; requestedTopic: string | null } | null {
  const topic = sanitizeRequestedTopic(rawService);
  if (!topic) return null;
  const exact = resolveBpoWizardService(topic);
  if (exact) return { service: exact, requestedTopic: null };
  // BLOG-ORIGIN-2510: `service=bpo` is the service family, not a page topic — keep the
  // service "to define" and let the real `page` param name the origin.
  if (topic === 'bpo') return { service: BPO_TO_DEFINE_ID, requestedTopic: null };
  return { service: BPO_TO_DEFINE_ID, requestedTopic: topic };
}

/** Human label stored on the lead (CRM) — never a package or price. */
export function bpoRequestLabel(serviceName: string | undefined, service: string, requestedTopic?: string | null): string {
  if (service === BPO_TO_DEFINE_ID) {
    const named = knownTopicName('en', requestedTopic);
    if (named) return `${named} — requested page: ${requestedTopic}`;
    return `BPO: to be defined together${requestedTopic ? ` — requested page: ${requestedTopic}` : ''}`;
  }
  return serviceName || 'BPO Service';
}

const TO_DEFINE_COPY: Record<string, { desc: string; page: string }> = {
  en: { desc: 'No matching service in the list? Describe your need — we scope it together in a written quote.', page: 'Page consulted' },
  fr: { desc: 'Aucun service de la liste ne correspond ? Décrivez votre besoin — nous le cadrons ensemble dans un devis écrit.', page: 'Page consultée' },
  nl: { desc: 'Geen passende dienst in de lijst? Beschrijf uw behoefte — we bepalen samen de scope in een schriftelijke offerte.', page: 'Bekeken pagina' },
  de: { desc: 'Kein passender Service in der Liste? Beschreiben Sie Ihren Bedarf — wir definieren ihn gemeinsam in einem schriftlichen Angebot.', page: 'Besuchte Seite' },
  es: { desc: '¿Ningún servicio de la lista encaja? Describa su necesidad: la definimos juntos en un presupuesto escrito.', page: 'Página consultada' },
  it: { desc: 'Nessun servizio della lista corrisponde? Descriva la sua esigenza: la definiamo insieme in un preventivo scritto.', page: 'Pagina consultata' },
  pt: { desc: 'Nenhum serviço da lista corresponde? Descreva a sua necessidade — definimo-la juntos num orçamento escrito.', page: 'Página consultada' },
  ar: { desc: 'لا توجد خدمة مطابقة في القائمة؟ صِف احتياجك — نحدده معًا في عرض سعر مكتوب.', page: 'الصفحة التي تمت زيارتها' },
  // START-I18N-2610: hu/vi/el — machine-translated, pending native review.
  hu: { desc: 'Nincs megfelelő szolgáltatás a listában? Írja le igényét — közösen határozzuk meg írásos árajánlatban.', page: 'Megtekintett oldal' },
  vi: { desc: 'Không có dịch vụ phù hợp trong danh sách? Hãy mô tả nhu cầu — chúng ta cùng xác định phạm vi trong báo giá bằng văn bản.', page: 'Trang đã xem' },
  el: { desc: 'Δεν υπάρχει κατάλληλη υπηρεσία στη λίστα; Περιγράψτε την ανάγκη σας — την καθορίζουμε μαζί σε γραπτή προσφορά.', page: 'Σελίδα που επισκεφτήκατε' },
};

export function toDefineCopy(lang: string) {
  return TO_DEFINE_COPY[(lang || 'en').slice(0, 2).toLowerCase()] || TO_DEFINE_COPY.en;
}
/**
 * Human, translated name of a "to define" page — display only. The stored
 * slug/payload never changes. Unknown slug → neutral wording, no guessed service.
 */
const TOPIC_NAMES: Record<string, Record<string, string>> = {
  'call-center': { en: 'Call Center & BPO', fr: 'Centre d’appels & BPO', nl: 'Callcenter & BPO', de: 'Callcenter & BPO', es: 'Call center y BPO', it: 'Call center e BPO', pt: 'Call center e BPO', ar: 'مركز الاتصال وBPO', ja: 'コールセンター＆BPO', zh: '呼叫中心与BPO', ko: '콜센터 및 BPO' },
  'ai-ops': { en: 'AI operations', fr: 'Opérations IA', nl: 'AI-operaties', de: 'KI-Operations', es: 'Operaciones de IA' },
  'customer-success': { en: 'Customer success', fr: 'Customer success', nl: 'Klantsucces', de: 'Kundenerfolg', es: 'Éxito del cliente' },
  'inbound': { en: 'Inbound calls', fr: 'Appels entrants', nl: 'Inkomende gesprekken', de: 'Inbound-Anrufe', es: 'Llamadas entrantes' },
  'outbound': { en: 'Outbound calls', fr: 'Appels sortants', nl: 'Uitgaande gesprekken', de: 'Outbound-Anrufe', es: 'Llamadas salientes' },
  'research': { en: 'Market research', fr: 'Études de marché', nl: 'Marktonderzoek', de: 'Marktforschung', es: 'Estudios de mercado' },
  'sdr': { en: 'Sales development (SDR)', fr: 'Prospection commerciale (SDR)', nl: 'Sales development (SDR)', de: 'Sales Development (SDR)', es: 'Desarrollo de ventas (SDR)' },
  'technical': { en: 'Technical support', fr: 'Support technique', nl: 'Technische ondersteuning', de: 'Technischer Support', es: 'Soporte técnico' },
  'telemarketing': { en: 'Telemarketing', fr: 'Télémarketing', nl: 'Telemarketing', de: 'Telemarketing', es: 'Telemarketing' },
  'telesales': { en: 'Telesales', fr: 'Télévente', nl: 'Televerkoop', de: 'Televerkauf', es: 'Televenta' },
  'compliance-monitoring': { en: 'Compliance monitoring', fr: 'Suivi de conformité', nl: 'Compliancemonitoring', de: 'Compliance-Überwachung', es: 'Supervisión del cumplimiento' },
  'customer-success-saas': { en: 'SaaS customer success', fr: 'Customer success SaaS', nl: 'SaaS-klantsucces', de: 'SaaS-Kundenerfolg', es: 'Éxito del cliente SaaS' },
  'email-support': { en: 'Email support', fr: 'Support par e-mail', nl: 'E-mailsupport', de: 'E-Mail-Support', es: 'Soporte por correo electrónico' },
  'finance-accounting': { en: 'Finance and accounting', fr: 'Finance et comptabilité', nl: 'Financiën en boekhouding', de: 'Finanzen und Buchhaltung', es: 'Finanzas y contabilidad' },
  'fraud-detection': { en: 'Fraud detection', fr: 'Détection de fraude', nl: 'Fraudedetectie', de: 'Betrugserkennung', es: 'Detección de fraude' },
  'healthcare-bpo': { en: 'Healthcare BPO', fr: 'BPO santé', nl: 'BPO voor de zorg', de: 'BPO im Gesundheitswesen', es: 'BPO sanitario' },
  'hr-payroll': { en: 'HR and payroll', fr: 'RH et paie', nl: 'HR en salarisadministratie', de: 'Personal und Lohnabrechnung', es: 'RR. HH. y nóminas' },
  'logistics-supply-chain': { en: 'Logistics and supply chain', fr: 'Logistique et chaîne d’approvisionnement', nl: 'Logistiek en supply chain', de: 'Logistik und Lieferkette', es: 'Logística y cadena de suministro' },
  'procurement-bpo-outsourcing': { en: 'Procurement BPO', fr: 'BPO achats', nl: 'Inkoop-BPO', de: 'Einkaufs-BPO', es: 'BPO de compras', it: 'BPO acquisti', pt: 'BPO de compras', ar: 'تعهيد المشتريات', ja: '調達BPO', zh: '采购BPO', ko: '구매 BPO' },
  'bpo-contact-center': { en: 'Contact Center Services', fr: 'Services de centre de contact', nl: 'Contactcenterdiensten', de: 'Contact-Center-Services', es: 'Servicios de contact center', it: 'Servizi di contact center', pt: 'Serviços de contact center', ar: 'خدمات مركز الاتصال', ja: 'コンタクトセンターサービス', zh: '联络中心服务', ko: '컨택센터 서비스' },
  'recruitment-staffing': { en: 'Recruitment and staffing', fr: 'Recrutement et placement', nl: 'Werving en selectie', de: 'Personalgewinnung', es: 'Reclutamiento y selección' },
  // LIVECHAT-2410: -bpo page variants of named topics — real name shown, service stays "to define" (no guessed match).
  'finance-accounting-bpo': { en: 'Finance and accounting', fr: 'Finance et comptabilité', nl: 'Financiën en boekhouding', de: 'Finanzen und Buchhaltung', es: 'Finanzas y contabilidad' },
  'hr-payroll-bpo': { en: 'HR and payroll', fr: 'RH et paie', nl: 'HR en salarisadministratie', de: 'Personal und Lohnabrechnung', es: 'RR. HH. y nóminas' },
  'recruitment-staffing-bpo': { en: 'Recruitment and staffing', fr: 'Recrutement et placement', nl: 'Werving en selectie', de: 'Personalgewinnung', es: 'Reclutamiento y selección' },
};
const TOPIC_NEUTRAL: Record<string, string> = {
  en: 'specific service page', fr: 'page de service spécifique', nl: 'specifieke dienstpagina', de: 'spezifische Serviceseite', es: 'página de servicio específica',
  it: 'pagina di servizio specifica', pt: 'página de serviço específica', ar: 'صفحة خدمة محددة',
  hu: 'konkrét szolgáltatási oldal', vi: 'trang dịch vụ cụ thể', el: 'συγκεκριμένη σελίδα υπηρεσίας',
};
/** Named topic (e.g. call-center) → its real name; unknown slug → null (never a guessed service). */
export function knownTopicName(lang: string, slug: string | null | undefined): string | null {
  if (!slug || !TOPIC_NAMES[slug]) return null;
  return topicDisplayName(lang, slug);
}
/** True when the slug is a wizard service or a named BPO topic (never a guess). */
export function isKnownBpoSlug(slug: string | null | undefined): boolean {
  return !!slug && (!!resolveBpoWizardService(slug) || !!TOPIC_NAMES[slug.trim().toLowerCase()]);
}
export function topicDisplayName(lang: string, slug: string | null | undefined): string | null {
  if (!slug) return null;
  const l = (lang || 'en').slice(0, 2).toLowerCase();
  const names = TOPIC_NAMES[slug];
  if (names) return names[l] || names.en;
  return TOPIC_NEUTRAL[l] || TOPIC_NEUTRAL.en;
}

/** Display-only: last safe path segment of a same-site `from` path (e.g. /fr/services/bpo/virtual-assistant → virtual-assistant). */
export function slugFromOriginPath(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  const seg = raw.split(/[?#]/)[0].split('/').filter(Boolean).pop();
  return sanitizeRequestedTopic(seg);
}


/** PILOT-1810h: human sector names for /industries/<slug> origin pages — display only. */
const SECTOR_NAMES: Record<string, Record<string, string>> = {
  finance: { en: 'Banking and finance', fr: 'Banque et finance', ar: 'البنوك والتمويل', es: 'Banca y finanzas', de: 'Banken und Finanzen', nl: 'Bankwezen en financiën' },
  banking: { en: 'Banking', fr: 'Banque', ar: 'البنوك', es: 'Banca', de: 'Bankwesen', nl: 'Bankwezen' },
  fintech: { en: 'Fintech', fr: 'Fintech', ar: 'التكنولوجيا المالية', es: 'Fintech', de: 'Fintech', nl: 'Fintech' },
  insurance: { en: 'Insurance', fr: 'Assurance', ar: 'التأمين', es: 'Seguros', de: 'Versicherungen', nl: 'Verzekeringen' },
  healthcare: { en: 'Healthcare', fr: 'Santé', ar: 'الرعاية الصحية', es: 'Salud', de: 'Gesundheitswesen', nl: 'Gezondheidszorg' },
  ecommerce: { en: 'E-commerce', fr: 'E-commerce', ar: 'التجارة الإلكترونية', es: 'Comercio electrónico', de: 'E-Commerce', nl: 'E-commerce' },
  telecom: { en: 'Telecom', fr: 'Télécommunications', ar: 'الاتصالات', es: 'Telecomunicaciones', de: 'Telekommunikation', nl: 'Telecom' },
  saas: { en: 'SaaS', fr: 'SaaS', ar: 'البرمجيات كخدمة', es: 'SaaS', de: 'SaaS', nl: 'SaaS' },
  'travel-hospitality': { en: 'Travel and hospitality', fr: 'Voyage et hôtellerie', ar: 'السفر والضيافة', es: 'Viajes y hostelería', de: 'Reisen und Gastgewerbe', nl: 'Reizen en horeca' },
  logistics: { en: 'Logistics', fr: 'Logistique', ar: 'الخدمات اللوجستية', es: 'Logística', de: 'Logistik', nl: 'Logistiek' },
  education: { en: 'Education', fr: 'Éducation', ar: 'التعليم', es: 'Educación', de: 'Bildung', nl: 'Onderwijs' },
  energy: { en: 'Energy', fr: 'Énergie', ar: 'الطاقة', es: 'Energía', de: 'Energie', nl: 'Energie' },
  automotive: { en: 'Automotive', fr: 'Automobile', ar: 'السيارات', es: 'Automoción', de: 'Automobil', nl: 'Automotive' },
  gaming: { en: 'Gaming', fr: 'Jeux vidéo', ar: 'الألعاب', es: 'Videojuegos', de: 'Gaming', nl: 'Gaming' },
  legal: { en: 'Legal', fr: 'Juridique', ar: 'القانون', es: 'Legal', de: 'Recht', nl: 'Juridisch' },
  'real-estate': { en: 'Real estate', fr: 'Immobilier', ar: 'العقارات', es: 'Inmobiliario', de: 'Immobilien', nl: 'Vastgoed' },
};
const SECTOR_PREFIX: Record<string, string> = { en: 'Sector', fr: 'Secteur', ar: 'القطاع', es: 'Sector', de: 'Branche', nl: 'Sector' };

/** Display-only sector label for a same-site /industries/<slug> path; null otherwise. */
export function sectorLabelFromPath(lang: string, raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return null;
  const seg = raw.split(/[?#]/)[0].split('/').filter(Boolean);
  const i = seg.indexOf('industries');
  if (i < 0 || !seg[i + 1]) return null;
  const key = seg[i + 1].toLowerCase().replace(/-support$/, '');
  const names = SECTOR_NAMES[key];
  if (!names) return null;
  const l = (lang || 'en').slice(0, 2).toLowerCase();
  const prefix = SECTOR_PREFIX[l] || SECTOR_PREFIX.en;
  return `${prefix} : ${names[l] || names.en}`.replace(' : ', l === 'fr' ? ' : ' : ': ');
}

/**
 * Display-only origin label. Priority: explicit `page`, else `from`. Only same-site paths
 * are humanised; tracking ids (bpo-hero-primary) and external URLs show nothing. Payload untouched.
 */
export function originLabelFromParams(
  lang: string,
  page: string | null | undefined,
  from: string | null | undefined,
  serviceName: (id: string) => string | undefined,
): string {
  const solution = solutionOriginLabel(lang, page);
  if (solution) return solution;
  const pricing = pricingOriginLabel(lang, page);
  if (pricing) return pricing;
  const country = countryOriginLabel(lang, page);
  if (country) return country;
  const home = homeOriginLabel(lang, page);
  if (home) return home;
  const sector = sectorLabelFromPath(lang, page) || (page ? null : sectorLabelFromPath(lang, from));
  if (sector) return sector;
  const slug = slugFromOriginPath(page) || slugFromOriginPath(from);
  if (!slug) return '';
  const exact = resolveBpoWizardService(slug);
  if (exact && serviceName(exact)) return serviceName(exact)!;
  // CTA-ALL-1910: only a /services/ page is a "specific service page"; city, blog or
  // landing pages get a neutral wording instead of an invented service.
  const path = (page || from || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '');
  if (TOPIC_NAMES[slug]) return topicDisplayName(lang, slug) || '';
  // AUTO-1910: an unknown non-service page shows its real path, never a generic wording.
  if (!path.startsWith('/services/')) return path || CONSULTED_ORIGIN[(lang || 'en').slice(0, 2)] || CONSULTED_ORIGIN.en;
  return topicDisplayName(lang, slug) || '';
}
const CONSULTED_ORIGIN: Record<string, string> = {
  en: 'page you were viewing', fr: 'page consultée', ar: 'الصفحة التي كنت تتصفحها', es: 'página consultada', de: 'besuchte Seite', nl: 'bekeken pagina', it: 'pagina consultata', pt: 'página consultada', ja: 'ご覧になっていたページ', zh: '您浏览的页面', ko: '보고 계시던 페이지', hu: 'az Ön által megtekintett oldal', vi: 'trang bạn đang xem', el: 'η σελίδα που βλέπατε',
};

// AUTO-1910: named solution pages shown as a human origin label (raw page kept for the CRM).
const SOLUTION_ORIGIN: Record<string, Record<string, string>> = {
  // DS-3010: Digital Studio IT hub — real page name instead of the generic "specific service page".
  '/services/digital-studio': {
    en: 'Digital Studio page', fr: 'page Digital Studio', es: 'página Digital Studio', de: 'Digital-Studio-Seite',
    it: 'pagina Digital Studio', pt: 'página Digital Studio', nl: 'Digital Studio-pagina',
  },
  '/solutions/bpo-contact-center': {
    en: 'BPO contact center solution', fr: 'solution BPO centre de contact', es: 'solución BPO de contact center', de: 'BPO-Contact-Center-Lösung',
    it: 'soluzione BPO contact center', pt: 'solução BPO de contact center', nl: 'BPO-contactcenteroplossing', pl: 'rozwiązanie BPO contact center',
    ru: 'решение BPO контакт-центра', tr: 'BPO çağrı merkezi çözümü', sv: 'BPO-kontaktcenterlösning', da: 'BPO-kontaktcenterløsning',
    ar: 'حل مركز الاتصال BPO', zh: 'BPO 联络中心解决方案', ja: 'BPOコンタクトセンター・ソリューション', ko: 'BPO 컨택센터 솔루션',
  },
};
export function solutionOriginLabel(lang: string, page: string | null | undefined): string {
  const path = (page || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '').replace(/\/+$/, '');
  const m = SOLUTION_ORIGIN[path];
  return m ? (m[(lang || 'en').slice(0, 2)] || m.en) : '';
}

const PRICING_ORIGIN: Record<string, string> = {
  en: 'pricing page', fr: 'page tarifs', ar: 'صفحة الأسعار', es: 'página de precios', de: 'Preisseite', nl: 'prijspagina', it: 'pagina dei prezzi', pt: 'página de preços', ja: '料金ページ', zh: '价格页面', ko: '요금 페이지',
};
/** Human label when the CTA came from /pricing (raw page/from stay untouched for the CRM). */
export function pricingOriginLabel(lang: string, page: string | null | undefined): string {
  const path = (page || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '').replace(/\/+$/, '');
  return path === '/pricing' ? (PRICING_ORIGIN[(lang || 'en').slice(0, 2)] || PRICING_ORIGIN.en) : '';
}

const COUNTRY_ORIGIN: Record<string, string> = {
  en: 'country page', fr: 'page pays', ar: 'صفحة البلد', es: 'página de país', de: 'Länderseite', nl: 'landenpagina', it: 'pagina paese', pt: 'página de país',
};
const COUNTRY_PAGE_RE = /^\/(call-center-for-[a-z-]+|bpo-(europe|asia|africa|latam|latin-america|middle-east|americas|north-america|oceania))$/;
/** Human label when the CTA came from a country/region landing page (raw page/from untouched). */
export function countryOriginLabel(lang: string, page: string | null | undefined): string {
  const path = (page || '').split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '').replace(/\/+$/, '');
  return COUNTRY_PAGE_RE.test(path) ? (COUNTRY_ORIGIN[(lang || 'en').slice(0, 2)] || COUNTRY_ORIGIN.en) : '';
}

const HOME_ORIGIN: Record<string, string> = {
  en: 'home page', fr: "page d'accueil", ar: 'الصفحة الرئيسية', es: 'página de inicio', de: 'Startseite', nl: 'startpagina', it: 'home page', pt: 'página inicial',
};
/** Human label when the CTA came from the home page ("/" or "/{lang}"). */
export function homeOriginLabel(lang: string, page: string | null | undefined): string {
  if (page == null || page === '') return '';
  const path = page.split('?')[0].replace(/^\/[a-z]{2}(?=\/|$)/, '').replace(/\/+$/, '');
  return path === '' ? (HOME_ORIGIN[(lang || 'en').slice(0, 2)] || HOME_ORIGIN.en) : '';
}

/**
 * START-SVC-2410: a /start deep link without `type=` (visitor picks "BPO
 * Services" afterwards) still carries its service. Only an exact catalogue
 * match is kept; an unknown value returns null (never guessed, never IT).
 */
export function bpoServiceFromUntypedLink(search: string): string | null {
  const p = new URLSearchParams(search);
  if (p.get('type')) return null;
  return resolveBpoWizardService(sanitizeRequestedTopic(p.get('service')));
}

/** CC-3010: title of the "to define" card/summary — the named topic (e.g. bpo-contact-center → Contact Center Services) when known, else the generic fallback. Display only; service id and slug unchanged. */
export function toDefineTitle(lang: string, requestedTopic: string | null | undefined, fallback: string): string {
  return knownTopicName(lang, requestedTopic) || fallback;
}

/** Quick-form display title: exact service name, known topic, generic "BPO Services" for a bare type=bpo link, else the to-define fallback. Display only. */
export function quickBpoServiceTitle(lang: string, service: string | undefined, requestedTopic: string | null | undefined, names: { serviceName?: string; bpoTitle: string; toDefine: string }): string {
  if (service && service !== BPO_TO_DEFINE_ID) return names.serviceName || service;
  const known = knownTopicName(lang, requestedTopic);
  if (known) return known;
  return requestedTopic ? names.toDefine : names.bpoTitle;
}
